import React, { useState, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { Fair } from '../types';
import { Link } from 'react-router-dom';

interface MapViewProps {
  fairs: Fair[];
}

const mapContainerStyle = {
  width: '100%',
  height: '600px'
};

const defaultCenter = {
  lat: 42.3601, // Boston area default
  lng: -71.0589
};

const MapView: React.FC<MapViewProps> = ({ fairs }) => {
  const [selectedFair, setSelectedFair] = useState<Fair | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);

    // Fit bounds to show all artist fairs
    if (fairs.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      fairs.forEach(stand => {
        if (stand.address?.coordinates?.lat && stand.address?.coordinates?.lng) {
          bounds.extend({ lat: stand.address.coordinates.lat, lng: stand.address.coordinates.lng });
        }
      });
      map.fitBounds(bounds);
    }
  }, [fairs]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Filter artist fairs that have coordinates
  const validFairs = fairs.filter(
    stand => stand.address?.coordinates?.lat && stand.address?.coordinates?.lng
  );

  if (validFairs.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
        <p className="text-yellow-800">
          No artist fairs with location data available for map view.
        </p>
      </div>
    );
  }

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
        <p className="text-yellow-800 font-semibold mb-2">
          Google Maps API Key Required
        </p>
        <p className="text-yellow-700 text-sm">
          Please add VITE_GOOGLE_MAPS_API_KEY to your .env file to enable map view.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <LoadScript googleMapsApiKey={apiKey}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
          }}
        >
          {validFairs.map(stand => (
            <Marker
              key={stand.id}
              position={{
                lat: stand.address.coordinates.lat,
                lng: stand.address.coordinates.lng
              }}
              onClick={() => setSelectedFair(stand)}
              title={stand.name}
            />
          ))}

          {selectedFair && selectedFair.address?.coordinates?.lat && selectedFair.address?.coordinates?.lng && (
            <InfoWindow
              position={{
                lat: selectedFair.address.coordinates.lat,
                lng: selectedFair.address.coordinates.lng
              }}
              onCloseClick={() => setSelectedFair(null)}
            >
              <div className="p-2 max-w-xs">
                <h3 className="font-bold text-lg mb-1">{selectedFair.name}</h3>
                <p className="text-sm text-gray-600 mb-2">
                  {selectedFair.address.street}<br />
                  {selectedFair.address.city}, {selectedFair.address.state} {selectedFair.address.zip}
                </p>
                {selectedFair.hours && (
                  <p className="text-sm mb-2">
                    <span className="font-semibold">Hours:</span> {selectedFair.hours}
                  </p>
                )}
                <Link
                  to={`/fair/${selectedFair.id}`}
                  className="inline-block bg-primary text-white px-3 py-1 rounded text-sm hover:bg-primary-dark transition-colors"
                >
                  View Details
                </Link>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default MapView;
